/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadOrders from "@insite/client-framework/Store/Data/Orders/Handlers/LoadOrders";
import { getOrdersDataView } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList from "@insite/content-library/Components/CardList";
import OrderSummaryCard, { OrderSummaryCardStyles } from "@insite/content-library/Components/OrderSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@monsma/XDL/Components/Notify";
import { widgetHeaderContentStyle } from "@monsma/XDL/Utilities/sharedStyles";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.customCSS]: string;
    };
}

const defaultCustomCss = `.card-list-heading{
}

.view-all-link{
}

.no-orders-found-grid-item{
}

.description-grid-item{
}

.no-orders-found-text{
}

.card_container__wrapper{
}

.order-summary-card-grid-item{
}

.order-summary-card-container{
}

.page-type-link-grid-item{
}

.order-number-grid-item{
}

.status-grid-item{
}

.web-order-number-grid-item{
}

.total-grid-item{
}`;

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        ordersDataView: getOrdersDataView(state, recentOrdersParameter),
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        settingsCollection: getSettingsCollection(state),
        canViewOrders: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadOrders,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;
// xng-change: adding new attributes for new structure
export interface RecentOrdersStyles {
    cardList?: GridContainerProps;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noOrdersFoundGridItem?: GridItemProps;
    noOrdersFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    orderSummaryCard?: OrderSummaryCardStyles;
    cardListGrid?: GridItemProps;
    tableGridItem?: GridItemProps;
    headerStyling?: InjectableCss;
    table?: DataTableProps;
}
// xng-change: adding related new style
export const recentOrdersStyles: RecentOrdersStyles = {
    cardList: {
        gap: 0,
        css: css`
            margin: 0 0 30px 0;
        `,
    },
    cardListGrid: {
        width: 12,
        css: css`
            display: block;
        `,
    },
    tableGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            overflow-x: auto;
            position: relative;
        `,
    },
    errorGridItem: { width: 12 },
    noOrdersFoundGridItem: { width: 12 },

    headerStyling: {
        css: css`
            ${widgetHeaderContentStyle}
        `,
    },
};

let recentOrdersParameter: GetOrdersApiParameter = {
    page: 1,
    customerSequence: "-1",
    sort: "OrderDate DESC",
};

const styles = recentOrdersStyles;

class RecentOrders extends React.Component<Props> {
    componentDidMount() {
        recentOrdersParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewOrders && !this.props.ordersDataView.value) {
            this.props.loadOrders(recentOrdersParameter);
        }
    }

    render() {
        const { ordersDataView, orderHistoryPageLink, settingsCollection, canViewOrders } = this.props;
        if (!canViewOrders || !ordersDataView.value) {
            return null;
        }

        const orderHistoryUrl = orderHistoryPageLink ? orderHistoryPageLink.url : undefined;

        const customCssWrapper = {
            css: css`
                ${this.props.fields.customCSS}
            `,
        };

        // xng-change: restructuring the content to be table view
        return (
            <StyledWrapper {...customCssWrapper}>
                <CardList 
                    {...styles.cardList}
                    data-test-selector="widget_RecentOrders"
                >
                    <StyledWrapper {...styles.headerStyling}>
                        <Typography variant="h4">{translate("Recent Orders")}</Typography>
                        <Link href={orderHistoryUrl}>{translate("View All Orders")}</Link>
                    </StyledWrapper>

                    {ordersDataView.value.length === 0 && (
                        <GridItem className="no-orders-found-grid-item" {...styles.noOrdersFoundGridItem}>
                            <Notify 
                                className="no-orders-found-text" 
                                messageType="danger" 
                                body={translate("No orders found")} 
                                hideCloseButton 
                                data-test-selector="recentOrders_NoOrdersFound"
                            />
                        </GridItem>
                    )}

                    {ordersDataView && ordersDataView.value && ordersDataView.value.length > 0 && (
                        <GridItem {...styles.tableGridItem}>
                            <DataTable {...styles.table}>
                                <DataTableHead>
                                    <DataTableHeader>{translate("Date")}</DataTableHeader>
                                    <DataTableHeader>{translate("Order #")}</DataTableHeader>
                                    <DataTableHeader>{translate("Ship To")}</DataTableHeader>
                                    <DataTableHeader>{translate("Status")}</DataTableHeader>
                                    <DataTableHeader>{translate("PO #")}</DataTableHeader>
                                    <DataTableHeader>{translate("Total")}</DataTableHeader>
                                </DataTableHead>
                                <DataTableBody>
                                    {ordersDataView.value.map(
                                        order =>
                                            order !== null && (
                                                <OrderSummaryCard
                                                    order={order}
                                                    key={order.id}
                                                    orderSettings={settingsCollection.orderSettings}
                                                    extendedStyles={styles.orderSummaryCard}
                                                />
                                            ),
                                    )}
                                </DataTableBody>
                            </DataTable>
                        </GridItem>
                    )}
                </CardList>
            </StyledWrapper>
        );
    }
}

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentOrders),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Orders Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
